// 통합 업무(Task)
//
// store/index.js 가 이 조각들을 펼쳐 하나의 루트 스토어로 합친다.
// Vuex 의 modules 옵션이 아니라 "조각"인 이유: Vuex 는 namespaced 여부와 무관하게
// 모듈 state 를 모듈 이름 아래에 중첩시킨다. 진짜 모듈로 바꾸면 store.state.tasks 가
// store.state.tasks.tasks 가 되어 소비자 40곳을 전부 고쳐야 한다.
// 조각 합성은 동작이 100% 동일하면서 파일만 나눈다.
//
// 액션은 실패를 그대로 던진다. 예전에는 액션마다 SET_ERROR 를 커밋했는데 그 상태를
// 읽는 화면이 하나도 없었다 — 사용자에게 보이는 오류 문구는 언제나 호출부의
// ElMessage 가 냈다. 쓰기만 하고 아무도 안 읽는 커밋 스무 개를 걷어냈다.

import http from '@/utils/http'

export const state = {
  tasks: [],
  currentTask: null,
  taskComments: [],
  taskActivities: [],
  taskSummary: null,
  taskBadges: { myOverdue: 0, unassignedVisible: 0 }
}

/*
 * 목록 카드 모양으로 맞춘다.
 *
 * 서버는 같은 업무를 두 모양으로 준다 — 목록(GET /api/tasks)은 supporters ·
 * commentCount · attachmentCount 를 싣고, 단건/변경 응답(TASK_INCLUDE)은
 * supporterLinks · attachments 를 싣는다. 보드에서 상태를 바꾸면 변경 응답이
 * 목록 행을 통째로 갈아 끼우는데, 그대로 넣으면 카드의 서포터 아바타와
 * 메모·첨부 개수가 그 순간 사라졌다(다음 새로고침 전까지).
 * 변경 응답에 없는 값은 이전 행에서 이어받는다.
 */
function toListShape(task, prev) {
  const next = { ...task }
  if (!Array.isArray(next.supporters)) {
    next.supporters = Array.isArray(task.supporterLinks)
      ? task.supporterLinks.map((l) => l.user).filter(Boolean)
      : (prev && prev.supporters) || []
  }
  if (next.attachmentCount == null) {
    next.attachmentCount = Array.isArray(task.attachments) ? task.attachments.length : (prev && prev.attachmentCount) || 0
  }
  if (next.commentCount == null) next.commentCount = (prev && prev.commentCount) || 0
  return next
}

export const mutations = {
  SET_TASKS(state, tasks) {
    state.tasks = tasks
  },

  ADD_TASK(state, task) {
    state.tasks.unshift(toListShape(task, null))
  },

  UPDATE_TASK(state, task) {
    const i = state.tasks.findIndex((t) => t.id === task.id)
    if (i !== -1) state.tasks.splice(i, 1, toListShape(task, state.tasks[i]))
    if (state.currentTask && state.currentTask.id === task.id) state.currentTask = task
  },

  /** 필드 몇 개만 그 자리에서 바꾼다 — 응답 전에 보드를 먼저 옮기고, 실패하면 되돌릴 때 */
  PATCH_TASK(state, { id, fields }) {
    const t = state.tasks.find((x) => x.id === id)
    if (t) Object.assign(t, fields)
    if (state.currentTask && state.currentTask.id === id) Object.assign(state.currentTask, fields)
  },

  DELETE_TASK(state, id) {
    state.tasks = state.tasks.filter((t) => t.id !== id)
    if (state.currentTask && state.currentTask.id === id) state.currentTask = null
  },

  SET_CURRENT_TASK(state, task) {
    state.currentTask = task
  },

  SET_TASK_COMMENTS(state, comments) {
    state.taskComments = comments
  },

  ADD_TASK_COMMENT(state, comment) {
    state.taskComments.push(comment)
  },

  UPDATE_TASK_COMMENT(state, comment) {
    const i = state.taskComments.findIndex((c) => c.id === comment.id)
    if (i !== -1) state.taskComments.splice(i, 1, comment)
  },

  DELETE_TASK_COMMENT(state, id) {
    state.taskComments = state.taskComments.filter((c) => c.id !== id)
  },

  SET_TASK_ACTIVITIES(state, activities) {
    state.taskActivities = activities
  },

  SET_TASK_SUMMARY(state, summary) {
    state.taskSummary = summary
  },

  SET_TASK_BADGES(state, badges) {
    state.taskBadges = badges
  }
}

function uploadForm(url, file, onProgress) {
  const formData = new FormData()
  formData.append('file', file)
  return http.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => { if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total)) }
  }).then((r) => r.data.attachment)
}

export const actions = {
  /*
   * 업무를 바꾸는 액션은 끝에 INVALIDATE_AGENDA 를 커밋한다. 홈 · 캘린더는 아젠다(일정+업무)를
   * 60초 캐시로 들고 있어서, 여기서 비우지 않으면 방금 옮기거나 지운 업무가 그쪽에 옛 모습으로 남는다.
   * (스토어가 조각을 합친 평평한 구조라 다른 조각의 뮤테이션도 이름으로 바로 커밋된다)
   */
  /*
   * 목록 조회. 결과를 스토어에 싣지 않고 돌려주기만 한다 — 업무 화면은 마지막 요청의
   * 응답만 SET_TASKS 로 싣고(Tasks.vue loadTasks), 홈 카드 같은 곳은 업무 화면 목록을
   * 덮지 않아야 해서다.
   */
  queryTasks(_, params = {}) {
    const query = { ...params }
    if (Array.isArray(query.status)) query.status = query.status.join(',')
    return http.get(`/api/tasks`, { params: query }).then((r) => r.data.tasks || [])
  },

  async fetchTask({ commit }, id) {
    const response = await http.get(`/api/tasks/${id}`)
    commit('SET_CURRENT_TASK', response.data.task)
    return response.data.task
  },

  async createTask({ commit }, data) {
    const response = await http.post(`/api/tasks`, data)
    if (response.data.task) commit('ADD_TASK', response.data.task)
    commit('INVALIDATE_AGENDA', {})
    return response.data
  },

  async updateTask({ commit }, { id, data }) {
    const response = await http.put(`/api/tasks/${id}`, data)
    commit('UPDATE_TASK', response.data.task)
    commit('INVALIDATE_AGENDA', {})
    return response.data.task
  },

  async deleteTask({ commit }, id) {
    await http.delete(`/api/tasks/${id}`)
    commit('DELETE_TASK', id)
    commit('INVALIDATE_AGENDA', {})
  },

  async claimTask({ commit }, id) {
    const response = await http.post(`/api/tasks/${id}/claim`)
    commit('UPDATE_TASK', response.data.task)
    commit('INVALIDATE_AGENDA', {})
    return response.data.task
  },

  async assignTask({ commit }, { id, assigneeId, reason }) {
    const response = await http.post(`/api/tasks/${id}/assign`, { assigneeId, reason })
    commit('UPDATE_TASK', response.data.task)
    commit('INVALIDATE_AGENDA', {})
    return response.data.task
  },

  async setTaskStatus({ commit, state }, { id, status }) {
    // 응답을 기다리지 않고 카드부터 옮긴다. 서버는 저장 · 활동 기록 · 알림까지 마치고
    // 답하므로, 기다리면 놓은 카드가 원래 칸에 머물다가 한참 뒤에 옮겨 간다.
    // 실패(결재 필요 409 포함)하면 원래 상태로 되돌린다.
    const cur = state.tasks.find((t) => t.id === id) ||
      (state.currentTask && state.currentTask.id === id ? state.currentTask : null)
    const prevStatus = cur ? cur.status : null
    if (cur && prevStatus !== status) commit('PATCH_TASK', { id, fields: { status } })
    try {
      const response = await http.patch(`/api/tasks/${id}/status`, { status })
      commit('UPDATE_TASK', response.data.task)
      commit('INVALIDATE_AGENDA', {})
      return response.data.task
    } catch (error) {
      if (cur && prevStatus !== status) commit('PATCH_TASK', { id, fields: { status: prevStatus } })
      // 승인이 필요한 업무를 바로 완료로 넘기려 한 경우.
      // 서버가 조용히 완료 요청으로 바꾸지 않고 409 를 주므로 여기서 한 번만 가로챈다.
      // 호출부는 catch 에서 `if (e.handled) return` 한 줄만 추가하면 된다.
      if (error.response?.status === 409 && error.response.data?.code === 'APPROVAL_REQUIRED') {
        const err = new Error('APPROVAL_REQUIRED')
        err.handled = true
        err.code = 'APPROVAL_REQUIRED'
        err.approver = error.response.data.approver
        err.taskId = id
        throw err
      }
      throw error
    }
  },

  async requestTaskCompletion({ commit }, { id, note, approverId }) {
    const response = await http.post(`/api/tasks/${id}/completion-request`, { note, approverId })
    commit('UPDATE_TASK', response.data.task)
    commit('INVALIDATE_AGENDA', {})
    return response.data
  },

  async approveTaskCompletion({ commit, dispatch }, { id, comment }) {
    const response = await http.post(`/api/tasks/${id}/completion-request/approve`, { comment })
    commit('UPDATE_TASK', response.data.task)
    commit('INVALIDATE_AGENDA', {})
    // 결재 배지는 보고서 + 업무 완료요청 합계다. 여기서 세지 않으면
    // 완료 요청을 승인해도 좌측 레일 숫자가 그대로 남는다.
    dispatch('fetchApprovalCount').catch(() => {})
    return response.data.task
  },

  async rejectTaskCompletion({ commit, dispatch }, { id, comment }) {
    const response = await http.post(`/api/tasks/${id}/completion-request/reject`, { comment })
    commit('UPDATE_TASK', response.data.task)
    commit('INVALIDATE_AGENDA', {})
    dispatch('fetchApprovalCount').catch(() => {})
    return response.data.task
  },

  async cancelTaskCompletion({ commit }, id) {
    const response = await http.post(`/api/tasks/${id}/completion-request/cancel`)
    commit('UPDATE_TASK', response.data.task)
    commit('INVALIDATE_AGENDA', {})
    return response.data.task
  },

  async forceCompleteTask({ commit }, { id, comment }) {
    const response = await http.post(`/api/tasks/${id}/force-complete`, { comment })
    commit('UPDATE_TASK', response.data.task)
    commit('INVALIDATE_AGENDA', {})
    return response.data.task
  },

  async setTaskApprover({ commit }, { id, approverId }) {
    const response = await http.patch(`/api/tasks/${id}/approver`, { approverId })
    commit('UPDATE_TASK', response.data.task)
    commit('INVALIDATE_AGENDA', {})
    return response.data.task
  },

  async fetchTaskCompletionRequests(_, id) {
    const response = await http.get(`/api/tasks/${id}/completion-requests`)
    return response.data.requests
  },

  async addTaskSupporter({ commit }, { id, userId }) {
    const response = await http.post(`/api/tasks/${id}/supporters`, { userId })
    commit('UPDATE_TASK', response.data.task)
    commit('INVALIDATE_AGENDA', {})
    return response.data.task
  },

  async removeTaskSupporter({ commit }, { id, userId }) {
    const response = await http.delete(`/api/tasks/${id}/supporters/${userId}`)
    commit('UPDATE_TASK', response.data.task)
    commit('INVALIDATE_AGENDA', {})
    return response.data.task
  },

  async fetchTaskComments({ commit }, id) {
    const response = await http.get(`/api/tasks/${id}/comments`)
    commit('SET_TASK_COMMENTS', response.data.comments || [])
    return response.data.comments || []
  },

  async addTaskComment({ commit }, { id, body }) {
    const response = await http.post(`/api/tasks/${id}/comments`, { body })
    commit('ADD_TASK_COMMENT', response.data.comment)
    return response.data.comment
  },

  async updateTaskComment({ commit }, { taskId, commentId, body }) {
    const response = await http.put(`/api/tasks/${taskId}/comments/${commentId}`, { body })
    commit('UPDATE_TASK_COMMENT', response.data.comment)
    return response.data.comment
  },

  async deleteTaskComment({ commit }, { taskId, commentId }) {
    await http.delete(`/api/tasks/${taskId}/comments/${commentId}`)
    commit('DELETE_TASK_COMMENT', commentId)
  },

  async fetchTaskActivities({ commit }, id) {
    const response = await http.get(`/api/tasks/${id}/activities`)
    commit('SET_TASK_ACTIVITIES', response.data.activities || [])
    return response.data.activities || []
  },

  async fetchTaskSummary({ commit }) {
    try {
      const response = await http.get(`/api/tasks/summary`)
      commit('SET_TASK_SUMMARY', response.data.summary)
      return response.data.summary
    } catch (error) {
      return null
    }
  },

  async fetchTaskBadges({ commit }) {
    try {
      const response = await http.get(`/api/tasks/badges`)
      commit('SET_TASK_BADGES', response.data.badges)
      return response.data.badges
    } catch (error) {
      return null
    }
  },

  uploadTaskAttachment(_, { taskId, file, onProgress }) {
    return uploadForm(`/api/tasks/${taskId}/attachments`, file, onProgress)
  },

  async deleteTaskAttachment(_, { taskId, attachmentId }) {
    await http.delete(`/api/tasks/${taskId}/attachments/${attachmentId}`)
  },

  uploadTaskCommentAttachment(_, { taskId, commentId, file, onProgress }) {
    return uploadForm(`/api/tasks/${taskId}/comments/${commentId}/attachments`, file, onProgress)
  },

  async deleteTaskCommentAttachment(_, { taskId, commentId, attachmentId }) {
    await http.delete(`/api/tasks/${taskId}/comments/${commentId}/attachments/${attachmentId}`)
  }
}

/*
 * 권한 판정은 server/middleware/taskAuth.js 와 **같은 규칙**이어야 한다.
 * 화면이 더 너그러우면 눌러 놓고 403 을 맞고, 더 깐깐하면 할 수 있는 일의 입구가 없다.
 * 예전에는 가져가기 판정이 카드 · 상세 · 홈 카드 세 곳에 복사돼 있었고,
 * 보드는 진행 권한(서포터 포함) 대신 관리 권한으로 상태 변경을 막아서
 * 서포터가 상세에서는 되는 상태 변경을 보드에서는 못 했다.
 */
const sameId = (a, b) => a != null && b != null && Number(a) === Number(b)

export const getters = {
  taskBadgeCount: (state) => state.taskBadges?.myOverdue || 0,
  unassignedTaskCount: (state) => state.taskBadges?.unassignedVisible || 0,

  /** 수정 · 배정 · 서포터 관리 (서버 canEditTask/canManageTask) */
  canManageTask: (state, getters) => (task) => {
    if (!state.user || !task) return false
    if (getters.isAdmin) return true
    return sameId(task.creatorId, state.user.id) || sameId(task.assigneeId, state.user.id)
  },

  /** 상태 · 첨부 · 메모 (서버 canProgressTask) — 서포터도 된다 */
  canProgressTask: (state, getters) => (task) => {
    if (!state.user || !task) return false
    if (getters.canManageTask(task)) return true
    // 목록 행은 supporters(사용자 배열), 단건은 supporterLinks(연결 행)를 싣는다
    const uid = state.user.id
    if ((task.supporters || []).some((u) => u && sameId(u.id, uid))) return true
    return (task.supporterLinks || []).some((l) => sameId(l.userId, uid) || (l.user && sameId(l.user.id, uid)))
  },

  /** 가져가기 (서버 canClaimTask) — 미배정이고, 부서가 정해져 있으면 그 부서원만 */
  canClaimTask: (state, getters) => (task) => {
    const u = state.user
    if (!u || !task) return false
    if (task.assigneeId != null || task.assignee) return false
    if (getters.isAdmin) return true
    if (task.departmentId == null) return true
    return sameId(task.departmentId, u.departmentId)
  }
}
